/**
 * Message Banner Component
 * Consolidated component for error, validation, and success messages
 */

import React, { memo } from "react";
import Notifications from "../../../components/common/Notifications";

interface MessageBannerProps {
  /** Error message from API (auto-dismiss) */
  error?: string | null;
  /** Validation error message (persistent) */
  validationError?: string | null;
  /** Success message (auto-dismiss) */
  successMessage?: string | null;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Displays error, validation, or success messages in a consistent format
 * Automatically determines the variant based on message type
 * 
 * @example
 * ```tsx
 * <MessageBanner
 *   error={error}
 *   validationError={validationError}
 *   successMessage={successMessage}
 * />
 * ```
 */
const MessageBanner: React.FC<MessageBannerProps> = memo(({
  error,
  validationError,
  successMessage, 
  className = "mt-2! mb-8!",
}) => {
  const successMessages = successMessage?.split("\n").filter(Boolean) ?? [];

  if (!validationError && !error && successMessages.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {validationError && (
        <Notifications messageInfo={validationError} variant="Error" />
      )}
      {!validationError && error && (
        <Notifications heading={error} variant="Error" />
      )}
      {successMessages.map((message) => (
        <Notifications
          key={message}
          variant={message.includes("no changes") ? "Alert" : "Success"}
          heading={message}
        />
      ))}
    </div>
  );
});

MessageBanner.displayName = "MessageBanner";

export default MessageBanner;
